import isNil from "lodash/isNil";
import { Option, OptionOriginal } from "PFTypes/options";
import { ReactNode } from "react";

import { Typography } from "../../typography";
import { SelectProps } from "../select.types";
import css from "./value_container.module.scss";
import { ValuesList } from "./values_list";

const isRenderingMultipleValues = <T extends OptionOriginal = OptionOriginal>(
  multiple: SelectProps<T>["multiple"],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  renderDisplayValue: SelectProps<T>["renderDisplayValue"]
): renderDisplayValue is (value: Option<T>[]) => ReactNode => !!multiple;

type UseDisplayValue<T> = Pick<SelectProps<T>, "multiple" | "renderDisplayValue" | "locked" | "onChange"> & {
  selectedOptions: Option<T>[];
  setDisplayValueRef?: (element: HTMLDivElement | null) => void;
};

type DisplayValueData = {
  displayElement: ReactNode;
};

export const useDisplayValue = <T extends OptionOriginal = OptionOriginal>({
  selectedOptions,
  multiple,
  renderDisplayValue,
  onChange,
  setDisplayValueRef
}: UseDisplayValue<T>): DisplayValueData => {
  const getDisplayValue = (): DisplayValueData => {
    if (renderDisplayValue) {
      const displayElement = isRenderingMultipleValues(multiple, renderDisplayValue)
        ? renderDisplayValue(selectedOptions)
        : renderDisplayValue(selectedOptions[0]);
      return {
        displayElement
      };
    }
    if (multiple) {
      return {
        displayElement: <ValuesList selectedOptions={selectedOptions} onChange={onChange} />
      };
    }
    const displayElement = selectedOptions[0]?.displayElement ?? selectedOptions[0]?.value;
    return {
      displayElement
    };
  };

  const { displayElement: displayElementValue } = getDisplayValue();

  if (isNil(displayElementValue)) {
    return { displayElement: null };
  }

  const displayElement =
    typeof displayElementValue === "string" ? (
      <Typography variant="bodyBold" textOverflow="clip" title={displayElementValue}>
        {displayElementValue}
      </Typography>
    ) : (
      <div ref={setDisplayValueRef} className={css.nonStringDisplayValue}>
        {displayElementValue}
      </div>
    );

  return { displayElement };
};
